/**
 * Post Create Setup
 *
 * Initializes the post create screen and wires shared collaborators together.
 */

import { PostCreateApiService } from "./post-create-api";
import type { PostCreateJsonValue } from "./post-create-api";
import type { PostCreateSubmitData } from "./post-create-api";
import type { PostCreateSubmitError } from "./post-create-api";
import { PostCreateForm, postCreateForm } from "./post-create-form";
import { PostCreateHandler } from "./post-create-handler";
import { PostCreateListener } from "./post-create-listener";
import { restoreSubmitButtonState } from "./post-create-render";
import { notifier, Notifier } from "../../core/notifier/notifier";
import { getEventScope } from "../../core/event-scope/event-scope";
import { attachEnterEscapeNavigation } from "../../core/keyboard-navigation/keyboard-navigation";
import { CommonUtils } from "../../util/common-utils";
import { formValidator } from "../../util/form-validator";
import { ImageSetup } from "../../util/media/image/image-setup";
import type { ImageHandler } from "../../util/media/image/image-handler";
import { OgpPreviewHandler } from "../../util/ogp/ogp-preview-handler";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { PostEditHandler } from "../edit/post-edit-handler";
import type { PostEditElements } from "../edit/post-edit-handler";
import { SNSPostSetup } from "../../sns/post/sns-post-setup";
import { PostCreateDraftHandler } from "./post-create-draft-handler";
import { PostProductLinker } from "../linker/product-linker-setup";
import { SearchMenuSetup } from "../../search/menu/search-menu-setup";
import { spinner } from "../../util/spinner/spinner";
import { ChoicesControl, initChoicesControls } from "../../components/choices-control/choices-control-setup";
import { BackNumberToggleHandler } from "../shared/back-number-toggle-handler";
import { PostContentCharCounterHandler } from "../shared/post-content-char-counter-handler";
import { PostSharedUtils } from "../util/post-shared-utils";
import { PostCreateUtils } from "./post-create-utils";
import { BoardId } from "../../constants/board-id";
import { Patterns } from "../../constants/patterns";
import { initAffiliateCopyButtons } from "../shared/affiliate-copy-handler";

type SubmissionData = PostCreateSubmitData & {
    boardId?: string;
    product_id?: number | null;
    productId?: number | null;
};

interface ValidationResult {
    isValid?: boolean;
    firstError?: string;
}

interface PostCreateSetupElements {
    form: HTMLFormElement | null;
    imageUpload: HTMLInputElement | null;
    videoUpload: HTMLInputElement | null;
    addImageBtn: HTMLElement | null;
    imageList: HTMLElement | null;
    videoList: HTMLElement | null;
    previewContainer: HTMLElement | null;
    titleInput: HTMLInputElement | HTMLTextAreaElement | null;
    contentTextarea: HTMLTextAreaElement | null;
    ratingSlider: HTMLInputElement | null;
    translationToggle: HTMLInputElement | null;
    submitBtn: HTMLButtonElement | HTMLInputElement | null;
    previewBtn: HTMLElement | null;
    addVideoBtn: HTMLElement | null;
    reviewAgreement: HTMLInputElement | null;
    snsLinksList: HTMLElement | null;
    youtubeSection: HTMLElement | null;
    youtubeContainer: HTMLElement | null;
    snsContainer: HTMLElement | null;
    ogpContainer: HTMLElement | null;
    [key: string]: PostCreateJsonValue | Element | null | undefined;
}

interface PostCreateInitResult {
    handler: PostCreateHandler;
    formManager: PostCreateForm;
    service: PostCreateApiService;
    elements: PostCreateSetupElements;
    imageHandler: ImageHandler | null;
    draft: PostCreateDraftHandler;
}

class PostCreateSetup {
    public static switchToFormTab(root: ParentNode | null | undefined): void {
        const searchRoot = root || document;
        const formTab = postCreateForm.getFormTab(searchRoot);

        if (
            formTab instanceof HTMLElement &&
            !formTab.classList.contains("is-active") &&
            formTab.getAttribute("aria-selected") !== "true"
        ) {
            formTab.click();
        }
    }

    public static isMeaningfulHtmlContent(html: string): boolean {
        try {
            const raw = String(html || "");
            const trimmed = raw.trim();
            if (!trimmed) return false;

            if (!trimmed.includes("<")) {
                return trimmed !== "";
            }

            const wrapper = document.createElement("div");
            wrapper.innerHTML = trimmed;

            if (postCreateForm.hasEmbeddedMedia(wrapper)) return true;

            const text = String(wrapper.textContent || "")
                .replace(/\u00a0/g, " ")
                .trim();
            return text !== "";
        } catch (_error) {
            return true;
        }
    }

    public static async init(
        rootEl: Element | HTMLElement | HTMLFormElement | null = null,
    ): Promise<PostCreateInitResult | null> {
        const form = (rootEl instanceof HTMLFormElement && rootEl) || postCreateForm.getPostCreateForm(document);

        if (!(form instanceof HTMLFormElement)) {
            console.warn("[PostCreateSetup] No form element found");
            return null;
        }

        PostCreateSetup.resetValidationStyles(form);
        PostCreateSetup.resetSubmitButtonLoader(form);
        PostCreateSetup.attachDirtyTracker(form);

        const keyboardNav = attachEnterEscapeNavigation(form, {
            isEscapeAllowedTarget: (element: EventTarget | null) => {
                if (element instanceof HTMLTextAreaElement && element.getAttribute("data-wysiwyg") === "true") {
                    return false;
                }
                return (
                    element instanceof HTMLInputElement ||
                    element instanceof HTMLSelectElement ||
                    element instanceof HTMLTextAreaElement
                );
            },
            selectAllOnMove: true,
        });
        keyboardNav.capture();

        // Title Enter → focus WYSIWYG editor (capture phase to override keyboard nav)
        form.addEventListener("keydown", (event) => {
            if (event.isComposing || event.key !== "Enter") return;
            const target = event.target;
            if (!(target instanceof HTMLInputElement) || target.type === "hidden") return;
            const titleField = postCreateForm.getTitleField(form);
            if (target !== titleField) return;
            const qlEditor = form.querySelector<HTMLElement>(".ql-editor");
            if (qlEditor instanceof HTMLElement) {
                event.preventDefault();
                event.stopImmediatePropagation();
                qlEditor.focus();
            }
        }, true);

        const elements = PostCreateSetup.initializeElements(rootEl);

        BackNumberToggleHandler.init(elements.form || (rootEl instanceof HTMLElement ? rootEl : document));

        const service = new PostCreateApiService();
        const formManager = new PostCreateForm();
        const handler = new PostCreateHandler();

        await Promise.all([service.init(), formManager.init()]);

        handler.elements = elements;
        handler.formManager = formManager;
        handler.apiService = service;

        handler.imageUploadHandler = handler.handleImageUpload.bind(handler);
        handler.videoUploadHandler = handler.handleVideoUpload.bind(handler);
        handler.previewHandler = handler.handlePreview.bind(handler);
        handler.translationToggleHandler = handler.handleTranslationToggle.bind(handler);
        handler.ratingChangeHandler = handler.handleRatingChange.bind(handler);
        handler.titleChangeHandler = handler.handleTitleChange.bind(handler);
        handler.contentChangeHandler = handler.handleContentChange.bind(handler);

        handler.starClickHandler = (event: Event) => {
            if (postCreateForm.getRatingStar(event)) {
                handler.handleStarClick(event);
            }
        };

        handler.starHoverHandler = (event: Event) => {
            if (postCreateForm.getRatingStar(event)) {
                handler.handleStarHover(event);
            }
        };

        handler.ratingMouseLeaveHandler = (event: Event) => {
            if (postCreateForm.getRatingInput(event)) {
                handler.handleRatingMouseLeave(event);
            }
        };

        handler.starKeydownHandler = (event: Event) => {
            if (postCreateForm.getRatingStar(event) || postCreateForm.getReviewQuestionAsEventTarget(event)) {
                handler.handleStarKeydown(event);
            }
        };

        const imageHandler = ImageSetup.initializeCreateMode("create", form, {
            maxFiles: 10,
            maxSize: "10MB",
            acceptedTypes: "image/*",
            existingImages: [],
        });
        handler.imageManager = imageHandler;

        PostCreateListener.setup(handler, elements);
        initChoicesControls(form);

        PostCreateSetup.setupFormHandling(elements, formManager, service);
        PostCreateSetup.setupReviewAgreement(elements);
        PostCreateSetup.setupReviewQuestionLanguage();
        PostCreateSetup.setupOgpPreview();
        PostCreateSetup.setupSnsPreview();
        PostCreateSetup.setupSnsLinksManager(elements.form);

        const draft = new PostCreateDraftHandler(form, elements);
        draft.attach();

        SearchMenuSetup.init(form);
        PostProductLinker.init(form);
        initAffiliateCopyButtons(form);

        PostContentCharCounterHandler.init(form);
        PostCreateSetup.setupProductSummaryToggle(form);

        requestAnimationFrame(() => {
            const firstQuestion = postCreateForm.getFirstReviewQuestion(form);
            if (firstQuestion instanceof HTMLElement) {
                firstQuestion.focus();
            } else {
                const titleField = postCreateForm.getTitleField(form);
                const isTitleVisible =
                    (titleField instanceof HTMLInputElement && titleField.type !== "hidden") ||
                    titleField instanceof HTMLTextAreaElement;
                if (isTitleVisible) {
                    titleField.focus();
                } else {
                    keyboardNav.focusFirstText();
                }
            }
        });

        return {
            handler,
            formManager,
            service,
            elements,
            imageHandler,
            draft,
        };
    }

    public static resetValidationStyles(form: HTMLFormElement): void {
        try {
            postCreateForm.getInvalidElements(form).forEach((element) => element.classList.remove("is-invalid"));
        } catch (_error) {
            void _error;
        }
    }

    public static attachDirtyTracker(form: HTMLFormElement): void {
        form.dataset.dirty = "0";

        if (form.dataset.dirtyTrackerAttached === "1") {
            return;
        }

        form.dataset.dirtyTrackerAttached = "1";

        const markDirty = () => {
            form.dataset.dirty = "1";
        };

        PostCreateListener.bindInput(form, markDirty, { capture: true });
        PostCreateListener.bindChange(form, markDirty, { capture: true });
    }

    public static setupProductSummaryToggle(root: HTMLElement | Document): void {
        const detailsList = postCreateForm.getProductSummaryDetails(root);
        detailsList.forEach((node) => {
            if (!(node instanceof HTMLDetailsElement)) return;
            if (node.dataset.animatedToggleBound === "1") return;

            const summary = postCreateForm.getProductSummaryToggle(node);
            const body = postCreateForm.getProductSummaryBody(node);
            if (!(summary instanceof HTMLElement) || !(body instanceof HTMLElement)) return;

            node.dataset.animatedToggleBound = "1";

            const measureHeight = () => {
                const inner = postCreateForm.getProductSummaryBodyInner(body);
                const contentHeight = inner instanceof HTMLElement ? inner.scrollHeight : body.scrollHeight;
                return Math.max(0, Math.ceil(contentHeight + 16));
            };

            const syncOpenHeight = () => {
                if (node.open) {
                    body.style.maxHeight = `${measureHeight()}px`;
                    body.style.opacity = "1";
                } else {
                    body.style.maxHeight = "0px";
                    body.style.opacity = "0";
                }
            };

            syncOpenHeight();

            PostCreateListener.bindClick(summary, (event) => {
                event.preventDefault();

                const willOpen = !node.open;
                if (willOpen) {
                    node.open = true;
                    body.style.maxHeight = "0px";
                    body.style.opacity = "0";

                    requestAnimationFrame(() => {
                        body.style.maxHeight = `${measureHeight()}px`;
                        body.style.opacity = "1";
                    });
                    return;
                }

                body.style.maxHeight = `${measureHeight()}px`;
                body.style.opacity = "1";

                requestAnimationFrame(() => {
                    body.style.maxHeight = "0px";
                    body.style.opacity = "0";
                });

                const onTransitionEnd = (transitionEvent: TransitionEvent) => {
                    if (transitionEvent.propertyName !== "max-height") return;
                    PostCreateListener.unbindTransitionEnd(body, onTransitionEnd);
                    node.open = false;
                };
                PostCreateListener.bindTransitionEnd(body, onTransitionEnd);
            });

            PostCreateListener.bindShowMoreToggle(node, () => {
                if (!node.open) return;
                requestAnimationFrame(() => {
                    body.style.maxHeight = `${measureHeight()}px`;
                });
            });

            const bodyInner = postCreateForm.getProductSummaryBodyInner(body);
            if (bodyInner instanceof HTMLElement && typeof ResizeObserver !== "undefined") {
                const resizeObserver = new ResizeObserver(() => {
                    if (!node.open) return;
                    body.style.maxHeight = `${measureHeight()}px`;
                });
                resizeObserver.observe(bodyInner);
            }

            PostCreateListener.bindWindowResize(() => {
                if (!node.open) return;
                body.style.maxHeight = `${measureHeight()}px`;
            });
        });
    }

    public static initializeElements(
        rootEl: Element | HTMLElement | HTMLFormElement | null = null,
    ): PostCreateSetupElements {
        const form = (rootEl instanceof HTMLFormElement && rootEl) || postCreateForm.getPostCreateForm(document);

        const domRoot: ParentNode = form || (rootEl instanceof HTMLElement ? rootEl : document);

        return {
            form,
            imageUpload: postCreateForm.getImageUpload(domRoot),
            videoUpload: postCreateForm.getVideoUpload(domRoot),
            addImageBtn: postCreateForm.getAddImageButton(domRoot),
            imageList: postCreateForm.getImageList(domRoot),
            videoList: postCreateForm.getVideoList(domRoot),
            previewContainer: postCreateForm.getPreviewRoot(domRoot),
            titleInput: postCreateForm.getTitleField(domRoot),
            contentTextarea: postCreateForm.getContentField(domRoot),
            ratingSlider: postCreateForm.getRatingSlider(domRoot),
            translationToggle: postCreateForm.getTranslationToggle(domRoot),
            submitBtn: postCreateForm.getSubmitTrigger(domRoot),
            previewBtn: postCreateForm.getPreviewButton(domRoot),
            addVideoBtn: postCreateForm.getAddVideoButton(domRoot),
            reviewAgreement: postCreateForm.getReviewAgreement(domRoot),
            snsLinksList: postCreateForm.getSnsLinksList(domRoot),
            youtubeSection: postCreateForm.getYoutubeSection(domRoot),
            youtubeContainer: postCreateForm.getYoutubeContainer(domRoot),
            snsContainer: postCreateForm.getSnsPreviewContainer(domRoot),
            ogpContainer: postCreateForm.getOgpPreviewContainer(domRoot),
        };
    }

    public static setupFormHandling(
        elements: PostCreateSetupElements,
        formManager: PostCreateForm,
        service: PostCreateApiService,
    ): void {
        if (!(elements.form instanceof HTMLFormElement)) {
            return;
        }

        elements.form.setAttribute("data-no-spinner", "true");

        let submitting = false;

        if (formValidator && !elements.form.hasAttribute("data-validation-attached")) {
            formValidator.attachToForm(elements.form, {
                showNotification: false,
                preventSubmit: true,
                realTimeValidation: true,
            });
            elements.form.setAttribute("data-validation-attached", "true");
        }

        PostCreateListener.bindSubmit(elements.form, async (event) => {
            event.preventDefault();

            if (submitting) {
                return;
            }

            const submitBtn = elements.form ? postCreateForm.getSubmitTrigger(elements.form) : null;

            try {
                if (formValidator && elements.form) {
                    const validation = formValidator.validate(elements.form, {
                        showNotification: false,
                    }) as ValidationResult;

                    if (!validation.isValid) {
                        PostCreateSetup.switchToFormTab(elements.form);
                        const message = validation.firstError || "required_field";
                        notifier.error(message, { duration: 5000 });
                        return;
                    }
                }

                try {
                    const backNumberToggle = postCreateForm.getBackNumberToggle(elements.form);
                    const createdAtInput = postCreateForm.getCreatedAtInput(elements.form);
                    if (backNumberToggle instanceof HTMLInputElement && backNumberToggle.checked) {
                        const value = String(
                            createdAtInput instanceof HTMLInputElement ? createdAtInput.value : "",
                        ).trim();
                        if (value === "") {
                            PostCreateSetup.switchToFormTab(elements.form);
                            notifier.error("back_number_date_required", { duration: 5000 });
                            return;
                        }
                    }
                } catch (_error) {
                    void _error;
                }

                submitting = true;

                const formData = formManager.getFormData(elements.form);
                const boardId = String(formData.get("board_id") || "").trim();
                const postTitleValue = String(formData.get("post_title") || "").trim();
                const requiresVisibleTitle = boardId !== BoardId.REVIEW;
                const contentField = formData.get("content");
                const snsLinksFields = SNSLinksUtils.normalizeSnsLinks(
                    formData
                        .getAll("sns_links[]")
                        .map((value) => (typeof value === "string" ? value : ""))
                        .filter(Boolean),
                );

                if (contentField !== null) {
                    const cleanedContent = PostSharedUtils.normalizePostContentHtml(
                        String(contentField || ""),
                        snsLinksFields,
                    );

                    if (requiresVisibleTitle && postTitleValue === "") {
                        PostCreateSetup.switchToFormTab(elements.form);
                        notifier.error("title_content_required", { duration: 5000 });
                        return;
                    }

                    if (!PostSharedUtils.hasNormalizedPostContentText(cleanedContent, snsLinksFields)) {
                        PostCreateSetup.switchToFormTab(elements.form);
                        notifier.error(requiresVisibleTitle ? "title_content_required" : "content_required", {
                            duration: 5000,
                        });
                        return;
                    }

                    if (!requiresVisibleTitle && postTitleValue === "") {
                        PostCreateSetup.switchToFormTab(elements.form);
                        notifier.error("title_content_required", { duration: 5000 });
                        return;
                    }

                    formData.set("content", cleanedContent);
                }

                if (submitBtn instanceof HTMLButtonElement) {
                    spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
                }

                await PostCreateSetup.compressFormDataImages(formData, service);
                await PostCreateSetup.readVideosToMemory(formData);
                const result = await service.submitPost(formData);

                if (result.success) {
                    PostCreateSetup.handleSubmissionSuccess(result.data || {}, formManager, elements.form);
                } else {
                    PostCreateSetup.handleSubmissionError(result.error);
                }
            } catch (error) {
                console.error("Form submission error:", error);
                PostCreateSetup.handleSubmissionError(
                    error instanceof Error || typeof error === "string" ? error : "post_create_failed",
                );
            } finally {
                submitting = false;
                PostCreateSetup.stopSpinners(elements);
            }
        });
    }

    public static setupReviewAgreement(elements: PostCreateSetupElements): void {
        const { submitBtn, reviewAgreement, form, previewContainer } = elements;
        if (!(reviewAgreement instanceof HTMLInputElement)) {
            return;
        }
        if (!(submitBtn instanceof HTMLButtonElement || submitBtn instanceof HTMLInputElement)) {
            return;
        }

        const updateSubmitButtonState = () => {
            const isAgreed = reviewAgreement.checked;

            submitBtn.disabled = !isAgreed;
            submitBtn.style.opacity = isAgreed ? "1" : "0.5";
            submitBtn.style.cursor = isAgreed ? "pointer" : "not-allowed";
        };

        updateSubmitButtonState();

        const switchToPreviewAndScroll = (event: Event) => {
            if (typeof event.isTrusted === "boolean" && !event.isTrusted) {
                return;
            }

            if (!reviewAgreement.checked) {
                return;
            }

            const root =
                (form instanceof HTMLElement && form) || postCreateForm.getPostCreateForm(reviewAgreement) || document;

            const previewTab = postCreateForm.getPreviewTab(root);

            if (
                previewTab &&
                !previewTab.classList.contains("is-active") &&
                previewTab.getAttribute("aria-selected") !== "true"
            ) {
                previewTab.click();
            }

            const scrollTarget =
                previewContainer || postCreateForm.getPreviewRoot(root) || postCreateForm.getPreviewPanel(root);

            if (!scrollTarget || typeof scrollTarget.scrollIntoView !== "function") {
                return;
            }

            setTimeout(() => {
                scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
        };

        PostCreateListener.bindChange(reviewAgreement, (event) => {
            updateSubmitButtonState();
            switchToPreviewAndScroll(event);
        });
    }

    public static handleSubmissionSuccess(
        data: SubmissionData,
        _formManager: PostCreateForm,
        form: HTMLFormElement | null,
    ): void {
        notifier.success("post_created_successfully");

        try {
            if (form) {
                form.setAttribute("data-draft-disabled", "true");
                form.dispatchEvent(new Event("post:create:draft:disable"));
            }
        } catch (_error) {
            void _error;
        }

        const boardIdInput = postCreateForm.getBoardIdInput(form);
        const boardIdFromForm = boardIdInput instanceof HTMLInputElement ? boardIdInput.value : null;
        const boardId = boardIdFromForm || form?.getAttribute?.("data-board-id") || data.board_id || data.boardId;
        if (boardId) {
            PostCreateDraftHandler.clearForBoard(String(boardId));
        }

        let productId = data.product_id ?? data.productId ?? null;
        let redirectProductId =
            Number.isInteger(productId) && productId !== null && productId > 0 ? String(productId) : "";

        if (boardId === BoardId.REVIEW || window.location.pathname.includes("/review/")) {
            if (!redirectProductId) {
                const pathMatch = window.location.pathname.match(Patterns.PATHNAME_REVIEW_PRODUCT_ID);
                if (pathMatch) {
                    redirectProductId = pathMatch[1];
                }
            }

            if (!redirectProductId) {
                const productInput = postCreateForm.getProductIdInput(form);
                if (productInput instanceof HTMLInputElement) {
                    redirectProductId = productInput.value;
                }
            }

            if (!redirectProductId && form instanceof HTMLFormElement) {
                const reviewProductInput = postCreateForm.getReviewProductIdInput(form);
                if (reviewProductInput instanceof HTMLInputElement) {
                    redirectProductId = reviewProductInput.value;
                }
            }

            if (redirectProductId) {
                window.location.href = CommonUtils.resolveUrl(`/products/${redirectProductId}`);
                return;
            }
        }

        window.location.href = CommonUtils.resolveUrl(`/posts/${String(boardId || BoardId.REVIEW)}`);
    }

    public static async compressFormDataImages(formData: FormData, service: PostCreateApiService): Promise<void> {
        const files = formData.getAll("attached_images[]");
        if (files.length === 0) return;

        formData.delete("attached_images[]");

        for (const entry of files) {
            if (!(entry instanceof File)) {
                formData.append("attached_images[]", entry);
                continue;
            }

            // Pre-read into memory via FileReader to avoid Android content:// URI failures in fetch()
            const inMemory = await PostCreateUtils.fileToMemory(entry);

            try {
                const compressed = await service.processImageFile(inMemory);
                if (compressed) {
                    const name = inMemory.name.replace(/\.[^.]+$/, ".jpg");
                    formData.append("attached_images[]", new File([compressed], name, { type: "image/jpeg" }));
                } else {
                    formData.append("attached_images[]", inMemory);
                }
            } catch (_e) {
                formData.append("attached_images[]", inMemory);
            }
        }
    }

    public static async readVideosToMemory(formData: FormData): Promise<void> {
        const files = formData.getAll("attached_videos[]");
        if (files.length === 0) return;

        formData.delete("attached_videos[]");

        for (const entry of files) {
            if (!(entry instanceof File)) {
                formData.append("attached_videos[]", entry);
                continue;
            }
            // Pre-read into memory to avoid Android content:// URI expiry failures in fetch()
            const inMemory = await PostCreateUtils.fileToMemory(entry);
            formData.append("attached_videos[]", inMemory);
        }
    }

    public static handleSubmissionError(error?: PostCreateSubmitError): void {
        const message =
            error instanceof Error ? error.message : typeof error === "string" ? error : "post_create_failed";
        notifier.error(message);

        if (error instanceof Error) {
            const detail = `[${error.name}] ${error.message}\n${error.stack ?? "(no stack)"}`;
            const toast = Notifier.showToast("error", " ", { duration: 60000 });
            const span = toast.querySelector("span");
            if (span instanceof HTMLElement) {
                span.textContent = detail;
                span.style.cssText =
                    "white-space:pre-wrap;font-size:11px;font-family:monospace;word-break:break-all;";
            }
            toast.style.maxHeight = "50vh";
            toast.style.overflowY = "auto";
            toast.style.maxWidth = "90vw";
        }
    }

    public static setupReviewQuestionLanguage(): void {
        const updateReviewQuestionLanguage = () => {
            const currentLang = localStorage.getItem("language") || "th";

            postCreateForm.getReviewQuestionLabels(document).forEach((label) => {
                if (!(label instanceof HTMLElement)) return;
                const textTh = label.getAttribute("data-text-th");
                const textEn = label.getAttribute("data-text-en");
                const textJa = label.getAttribute("data-text-ja");

                let newText = "";
                if (currentLang === "th" && textTh) {
                    newText = textTh;
                } else if (currentLang === "en" && textEn) {
                    newText = textEn;
                } else if (currentLang === "ja" && textJa) {
                    newText = textJa;
                } else {
                    newText = textTh || textEn || textJa || "Question";
                }

                label.textContent = newText;
            });

            postCreateForm.getReviewSingleSelectWrappers(document).forEach((wrap) => {
                if (!(wrap instanceof HTMLElement)) return;

                if (!wrap.dataset.singleSelectBound) {
                    wrap.dataset.singleSelectBound = "1";

                    PostCreateListener.bindChange(wrap, (event) => {
                        const radio = postCreateForm.getEventRadio(event);
                        if (!(radio instanceof HTMLInputElement)) return;

                        const hidden = postCreateForm.getHiddenNamedInput(wrap);
                        if (hidden instanceof HTMLInputElement) {
                            hidden.value = radio.value;
                            hidden.dispatchEvent(new Event("input", { bubbles: true }));
                            hidden.dispatchEvent(new Event("change", { bubbles: true }));
                        }

                        postCreateForm.getReviewOptionLabels(wrap).forEach((label) => {
                            if (!(label instanceof HTMLElement)) return;
                            label.classList.toggle("selected", !!postCreateForm.getCheckedRadio(label));
                        });
                    });
                }

                const parseOptions = (attr: string): PostCreateJsonValue[] => {
                    const raw = wrap.getAttribute(attr);
                    if (!raw) return [];
                    try {
                        const parsed = JSON.parse(raw) as PostCreateJsonValue;
                        return Array.isArray(parsed) ? parsed : [];
                    } catch {
                        return [];
                    }
                };

                const optionsTh = parseOptions("data-options-th");
                const optionsEn = parseOptions("data-options-en");
                const optionsJa = parseOptions("data-options-ja");

                let options = currentLang === "en" ? optionsEn : currentLang === "ja" ? optionsJa : optionsTh;
                if (!Array.isArray(options) || options.length === 0) {
                    options = optionsTh.length ? optionsTh : optionsEn.length ? optionsEn : optionsJa;
                }

                postCreateForm.getReviewOptionTexts(wrap).forEach((span) => {
                    if (!(span instanceof HTMLElement)) return;
                    const idx = Number(span.dataset.optionIndex);
                    if (!Number.isFinite(idx)) return;
                    if (options[idx] !== undefined) {
                        span.textContent = String(options[idx]);
                    }
                });
            });

            postCreateForm.getReviewMultiSelects(document).forEach((select) => {
                if (!(select instanceof HTMLSelectElement)) return;

                const parseOptions = (attr: string): PostCreateJsonValue[] => {
                    const raw = select.getAttribute(attr);
                    if (!raw) return [];
                    try {
                        const parsed = JSON.parse(raw) as PostCreateJsonValue;
                        return Array.isArray(parsed) ? parsed : [];
                    } catch {
                        return [];
                    }
                };

                const optionsTh = parseOptions("data-options-th");
                const optionsEn = parseOptions("data-options-en");
                const optionsJa = parseOptions("data-options-ja");

                let options = currentLang === "en" ? optionsEn : currentLang === "ja" ? optionsJa : optionsTh;
                if (!Array.isArray(options) || options.length === 0) {
                    options = optionsTh.length ? optionsTh : optionsEn.length ? optionsEn : optionsJa;
                }

                Array.from(select.options).forEach((option) => {
                    const idx = Number(option.value);
                    if (!Number.isFinite(idx)) return;
                    if (options[idx] !== undefined) {
                        option.textContent = String(options[idx]);
                    }
                });

                try {
                    ChoicesControl.destroyOne(select);
                } catch {
                    void 0;
                }
                const root = postCreateForm.getChoicesHost(select);
                ChoicesControl.init((root || document) as ParentNode);
            });
        };

        setTimeout(() => {
            updateReviewQuestionLanguage();
        }, 100);

        const docScope = getEventScope(document, "post-create");
        docScope.once("languageChanged", () => {
            docScope.on("languageChanged", () => {
                updateReviewQuestionLanguage();
            });
        });
    }

    public static setupOgpPreview(): void {
        const ogpPreview = new OgpPreviewHandler();
        const formRoot = postCreateForm.getPostCreateForm(document);
        const root = formRoot || document;

        const contentTextarea = postCreateForm.getContentField(root);
        const ogpPreviewContainer = postCreateForm.getOgpPreviewContainer(root);

        if (contentTextarea instanceof HTMLTextAreaElement && ogpPreviewContainer instanceof HTMLElement) {
            ogpPreview.init(contentTextarea, ogpPreviewContainer);
        }
    }

    public static setupSnsPreview(): void {
        return;
    }

    public static setupSnsLinksManager(formEl: HTMLFormElement | null = null): void {
        const formRoot = (formEl instanceof HTMLFormElement && formEl) || postCreateForm.getPostCreateForm(document);
        if (!formRoot) return;

        const snsLinksList = postCreateForm.getSnsLinksList(formRoot);
        if (!snsLinksList) {
            return;
        }

        const panel = postCreateForm.getSnsLinksPanel(formRoot);
        const postKey = panel?.getAttribute("data-post-id") || "create";

        const handler = new PostEditHandler();
        const handlerElements: PostEditElements = {
            snsLinksList,
            youtubeSection: postCreateForm.getYoutubeSection(formRoot),
            youtubeContainer: postCreateForm.getYoutubeContainer(formRoot),
            snsContainer: postCreateForm.getSnsPreviewContainer(formRoot),
            ogpContainer: postCreateForm.getOgpPreviewContainer(formRoot),
            snsTabContainer: postCreateForm.getSnsPreviewTab(formRoot),
            ogpTabContainer: postCreateForm.getOgpPreviewTab(formRoot),
            formContainer: formRoot,
            contentTextarea: postCreateForm.getContentField(formRoot),
            previewRoot: postCreateForm.getPreviewRoot(formRoot),
        };
        handler.elements = handlerElements;

        SNSPostSetup.registerListeners({
            postKey,
            rootEl: formRoot,
            handler,
        });

        SNSPostSetup.wireSnsLinksManager({
            postKey,
            formRoot,
            handler,
            elements: handler.elements,
            initialSyncDelayMs: 100,
        });
    }

    public static stopSpinners(elements: PostCreateSetupElements): void {
        try {
            if (elements.form) {
                PostCreateSetup.resetSubmitButtonLoader(elements.form);
            }
            spinner.hidePageLoader();
        } catch (error) {
            console.error("Error stopping spinners:", error);
        }
    }

    public static resetSubmitButtonLoader(form: HTMLFormElement | null | undefined): void {
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        const submitBtn = postCreateForm.getSubmitTrigger(form);
        if (!(submitBtn instanceof HTMLButtonElement)) {
            return;
        }

        spinner.hideButtonLoader(submitBtn);

        const spinnerElements = postCreateForm.getSpinnerElements(submitBtn);
        spinnerElements.forEach((spinnerElement) => {
            spinnerElement.remove();
        });

        if (submitBtn.hasAttribute("data-original-text")) {
            const originalText = submitBtn.getAttribute("data-original-text");
            const originalDisabled = submitBtn.getAttribute("data-original-disabled") === "true";
            restoreSubmitButtonState(submitBtn, originalText, originalDisabled);
        }
    }

}

export { PostCreateSetup };
